import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { User, Menu, Home, Settings, HelpCircle, ExternalLink, Globe, CircleDollarSign, Link, ChevronDown, Rocket, Target, Calculator, Wallet, DollarSign, Boxes, Calendar, StopCircle, AlertTriangle, Pin } from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';

export const InstructionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openSubIndex, setOpenSubIndex] = useState<number | null>(null);
  const [openParamIndex, setOpenParamIndex] = useState<number | null>(null);

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');

  function renderWithIcons(text: string) {
    return text.split(/(<br\/>|<AlertTriangle \/>|<PinIcon \/>|<strong>.*?<\/strong>|<a href=".*?">.*?<\/a>)/g).map((part, idx) => {
      if (part === '<br/>') return <br key={idx} />;
      if (part === '<AlertTriangle />') return <AlertTriangle key={idx} className="inline w-5 h-5 text-yellow-500 align-text-bottom mx-1" />;
      if (part === '<PinIcon />') return <Pin key={idx} className="inline w-5 h-5 text-blue-500 align-text-bottom mx-1" />;
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const inner = part.replace(/^<strong>/, '').replace(/<\/strong>$/, '');
        return <strong key={idx}>{inner}</strong>;
      }
      if (part.startsWith('<a href="') && part.endsWith('</a>')) {
        const hrefMatch = part.match(/href="([^"]*)"/);
        const textMatch = part.match(/>([^<]*)<\/a>/);
        if (hrefMatch && textMatch) {
          return (
            <a
              key={idx}
              href={hrefMatch[1]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0"
            >
              {textMatch[1]}
            </a>
          );
        }
      }
      return part;
    });
  }


  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    { id: 'eng', label: 'English', onClick: () => switchLanguage('ENG') },
    { id: 'rus', label: 'Русский', onClick: () => switchLanguage('RUS') }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  return (
    <div className="min-h-screen bg-background" 
         style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem' }}>
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              <Dropdown id="account" trigger={<User className="w-5 h-5 text-iconPrimary" />} items={accountItems} align="right" />
              <Dropdown id="language" trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>} items={languageItems} align="right" />
              <Dropdown id="pages" trigger={<Menu className="w-5 h-5 text-iconPrimary" />} items={pagesItems} align="right" />
          </div>
        </div>
      </header>
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-text mb-8 md:mb-12 text-center font-sans">
          <div className="flex items-center justify-center gap-3">
            <Settings className="w-7 h-7 text-primary" />
          {t('instructions')}
          </div>
        </h2>
        <div className="text-lg md:text-2xl text-textSubtle text-center mb-8 font-sans whitespace-pre-line">
          {t('instructions_description')}
        </div>
        {/* Accordions, same styling as FAQPage */}
        <div className="flex flex-col gap-4 w-full">
          {/* Accordion 1 */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openIndex === 0 ? 'shadow-lg button-glow' : ''}`}
              onClick={() => setOpenIndex(openIndex === 0 ? null : 0)}
              aria-expanded={openIndex === 0}
            >
              <span className="text-left">{t('instructions_acc1_title')}</span>
              <span className={`ml-2 transition-transform ${openIndex === 0 ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>
            {openIndex === 0 && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in whitespace-pre-line">
                {renderWithIcons(t('instructions_acc1_body'))}
              </div>
            )}
          </div>
          {/* Accordion 2 */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openIndex === 1 ? 'shadow-lg button-glow' : ''}`}
              onClick={() => setOpenIndex(openIndex === 1 ? null : 1)}
              aria-expanded={openIndex === 1}
            >
              <span className="text-left">{t('instructions_acc2_title')}</span>
              <span className={`ml-2 transition-transform ${openIndex === 1 ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>
            {openIndex === 1 && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in whitespace-pre-line">
                {t('instructions_acc2_body')}
              </div>
            )}
          </div>
          {/* Accordion 3 */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openIndex === 2 ? 'shadow-lg button-glow' : ''}`}
              onClick={() => {
                if (openIndex === 2) {
                  setOpenIndex(null);
                  setOpenSubIndex(null);
                } else {
                  setOpenIndex(2);
                  setOpenSubIndex(null);
                }
              }}
              aria-expanded={openIndex === 2}
            >
              <span className="text-left">{t('instructions_acc3_title')}</span>
              <span className={`ml-2 transition-transform ${openIndex === 2 ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>
            {openIndex === 2 && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                <div className="text-textSubtle mb-3">{t('instructions_acc3_intro')}</div>
                <div className="flex flex-col gap-3">
                  {/* Sub-accordions for commands */}
                  <details className={`group instructions-subacc`} open={openSubIndex === 0}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 0 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===0?null:0);}}>
                      <span className="flex items-center gap-2"><Rocket className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Start auto-trading' : 'Запустить автоторговлю'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 0 && (
                    <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                      {renderWithIcons(t('instructions_cmd_start_body'))}
                    </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 1}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 1 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===1?null:1);}}>
                      <span className="flex items-center gap-2"><Target className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'One-time order' : 'Разовый ордер'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 1 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {t('instructions_cmd_one_time_body')}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 2}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 2 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===2?null:2);}}>
                      <span className="flex items-center gap-2"><Calculator className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Statistics' : 'Статистика'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 2 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {renderWithIcons(t('instructions_cmd_statistics_body'))}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 3}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 3 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===3?null:3);}}>
                      <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Balance' : 'Баланс'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 3 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {t('instructions_cmd_balance_body')}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 4}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 4 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===4?null:4);}}>
                      <span className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Current price' : 'Текущая цена'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 4 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {t('instructions_cmd_current_price_body')}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 5}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 5 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===5?null:5);}}>
                      <span className="flex items-center gap-2"><Boxes className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Open orders' : 'Открытые ордера'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 5 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {t('instructions_cmd_open_orders_body')}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 6}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 6 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===6?null:6); if(openSubIndex===6) setOpenParamIndex(null);}}>
                      <span className="flex items-center gap-2"><Settings className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Parameters' : 'Параметры'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 6 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3">
                        <div className="mb-4 text-text whitespace-pre-line">
                          {t('instructions_cmd_parameters_intro')}
                        </div>
                        <div className="space-y-2">
                          <details className={`group instructions-subacc`} open={openParamIndex === 0}>
                            <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openParamIndex === 0 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenParamIndex(openParamIndex===0?null:0);}}>
                              <span>{t('instructions_param_buy_amount')}</span>
                              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            </summary>
                            {openParamIndex === 0 && (
                              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                {renderWithIcons(t('instructions_param_buy_amount_body'))}
                              </div>
                            )}
                          </details>
                          <details className={`group instructions-subacc`} open={openParamIndex === 1}>
                            <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openParamIndex === 1 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenParamIndex(openParamIndex===1?null:1);}}>
                              <span>{t('instructions_param_profit_percent')}</span>
                              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            </summary>
                            {openParamIndex === 1 && (
                              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                {renderWithIcons(t('instructions_param_profit_percent_body'))}
                              </div>
                            )}
                          </details>
                          <details className={`group instructions-subacc`} open={openParamIndex === 2}>
                            <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openParamIndex === 2 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenParamIndex(openParamIndex===2?null:2);}}>
                              <span>{t('instructions_param_auto_buy_growth')}</span>
                              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            </summary>
                            {openParamIndex === 2 && (
                              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                {renderWithIcons(t('instructions_param_auto_buy_growth_body'))}
                              </div>
                            )}
                          </details>
                          <details className={`group instructions-subacc`} open={openParamIndex === 3}>
                            <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openParamIndex === 3 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenParamIndex(openParamIndex===3?null:3);}}>
                              <span>{t('instructions_param_auto_buy_fall')}</span>
                              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            </summary>
                            {openParamIndex === 3 && (
                              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                {renderWithIcons(t('instructions_param_auto_buy_fall_body'))}
                              </div>
                            )}
                          </details>
                          <details className={`group instructions-subacc`} open={openParamIndex === 4}>
                            <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openParamIndex === 4 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenParamIndex(openParamIndex===4?null:4);}}>
                              <span>{t('instructions_param_commission')}</span>
                              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            </summary>
                            {openParamIndex === 4 && (
                              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                {t('instructions_param_commission_body')}
                              </div>
                            )}
                          </details>
                        </div>
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 7}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 7 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===7?null:7);}}>
                      <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Subscription' : 'Подписка'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 7 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {renderWithIcons(t('instructions_cmd_subscription_body'))}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 8}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 8 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===8?null:8);}}>
                      <span className="flex items-center gap-2"><StopCircle className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Stop auto-trading' : 'Остановить автоторговлю'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 8 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {renderWithIcons(t('instructions_cmd_stop_body'))}
                      </div>
                    )}
                  </details>
                  <details className={`group instructions-subacc`} open={openSubIndex === 9}>
                    <summary className={`flex items-center justify-between w-full px-3 py-2 rounded-md border border-border bg-card text-left text-text text-sm md:text-base font-semibold cursor-pointer list-none transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${openSubIndex === 9 ? 'shadow-lg button-glow' : ''}`} onClick={(e)=>{e.preventDefault(); setOpenSubIndex(openSubIndex===9?null:9);}}>
                      <span className="flex items-center gap-2"><HelpCircle className="w-4 h-4 text-primary" />{i18n.language === 'ENG' ? 'Help' : 'Помощь'}</span>
                      <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    </summary>
                    {openSubIndex === 9 && (
                      <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                        {t('instructions_cmd_help_body')}
                      </div>
                    )}
                  </details>
                </div>
              </div>
            )}
          </div>
          {/* Accordion 4 - PWA Installation */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openIndex === 3 ? 'shadow-lg button-glow' : ''}`}
              onClick={() => setOpenIndex(openIndex === 3 ? null : 3)}
              aria-expanded={openIndex === 3}
            >
              <span className="text-left">{t('instructions_acc4_title')}</span>
              <span className={`ml-2 transition-transform ${openIndex === 3 ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>
            {openIndex === 3 && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in whitespace-pre-line">
                {t('instructions_acc4_body')}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}; 